import { useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { IconName } from '@hotjar/hotsauce';
import { Alert, Icon, PlanBadge, Text, tokens } from '@hotjar/hotsauce';

import type { Site, SiteId } from '@hotjar/site';
import { ZapierFeature } from '@hotjar/integrations';
import { ErrorBoundary } from '@hotjar/error-handler';
import type { ProductType } from '@hotjar/webapp/domains/common/product/types';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { ZapierTemplate, prefilledTemplates } from './ZapierTemplate';

type WidgetId = number;

const Wrapper = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${tokens.spacer.x1pt5};
`;

const Logo = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
  display: inline-block;
  vertical-align: middle;
`;

const Title = styled(Text)`
  display: inline-block;
  vertical-align: middle;
`;

const PlanBadgeAlignedToLeft = styled(PlanBadge)`
  margin-left: ${tokens.spacer.x1};
  margin-right: auto;
`;

const AlertWithMargin = styled(Alert)`
  margin-top: ${tokens.spacer.x1pt5};
`;

export interface ForwardToZapierProps {
  disabledTooltip: string;
  // FIXME: Use this prop or remove it if not needed.
  // eslint-disable-next-line react/no-unused-prop-types
  siteId: SiteId;
  widgetId: WidgetId | null;
  // FIXME: Use this prop or remove it if not needed.
  // eslint-disable-next-line react/no-unused-prop-types
  widgetType: 'incoming_feedback' | 'poll' | 'recording_segment';
  site: Site;
  title: string;
  upsell: (referrer: KnownReferrer) => void;
  contextOfUse: ProductType;
  trackingState?: string;
  isSurvey?: boolean;
}

export const ForwardToZapier = ({
  disabledTooltip: widgetNotCreatedYetTooltip,
  widgetId,
  site,
  title,
  upsell,
  contextOfUse,
  trackingState,
  isSurvey = false,
}: ForwardToZapierProps): JSX.Element => {
  const { t } = useTranslation();
  const planEnough = ZapierFeature.canUse(contextOfUse, site);
  const widgetNotCreatedYet = widgetId === null;
  const featureGroupBadgeVisible = !planEnough;
  const disabledAlert = featureGroupBadgeVisible
    ? t('integrations:business-feature')
    : widgetNotCreatedYetTooltip;

  const { click: trackClick } = useTrack();

  const trackZapier = useCallback(
    (name) => {
      const uiPosition = name === 'Zapier' ? 'Forward to Zapier' : `Forward to Zapier - ${name}`;
      trackClick('Forward Link', uiPosition, {
        state_event_was_triggered_on: trackingState,
      });
    },
    [trackClick, trackingState],
  );

  const triggerUpsell = useCallback(() => {
    upsell(`${contextOfUse}.integrations_zapier`);
  }, [contextOfUse, upsell]);

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <Wrapper>
        <header>
          <Logo icon="zapier_small" size="m" />
          <Title as="span" textType="body2">
            {title}
          </Title>
        </header>
        {featureGroupBadgeVisible && (
          <PlanBadgeAlignedToLeft
            kind={ZapierFeature.FEATURE_GROUP}
            size="s"
            onClick={triggerUpsell}
          />
        )}
      </Wrapper>
      {prefilledTemplates.map(({ name, icon, surveyUrl, feedbackUrl, copy }, index) => (
        <ZapierTemplate
          key={name}
          icon={icon as IconName}
          url={isSurvey ? surveyUrl : feedbackUrl}
          copy={t(copy)}
          isLastTemplate={index === prefilledTemplates.length - 1}
          onClick={() => trackZapier(name)}
        />
      ))}
      {widgetNotCreatedYet || !planEnough ? (
        <AlertWithMargin kind="info">{disabledAlert}</AlertWithMargin>
      ) : null}
    </ErrorBoundary>
  );
};
